import { LoadingOverlay, Stack } from '@mantine/core'
import { ReactQuery } from 'cms-api'
import React from 'react'
import { getPublicLayout } from '@public/components/PublicLayout'
import { Box } from 'components/Layout'
import {
  StrapiHeroWidget,
  StrapiTradeAdvantagesWidget,
  StrapiExpertTestimonialsWidget,
  StrapiBrandsWidget,
  StrapiFeaturedExpertsWidget,
  StrapiTrendingProductsWidget,
  StrapiFeaturedCategoriesWidget,
} from 'components/Widgets'
import { STRAPI_WIDGETS } from 'constants/common'
import { prepareGetStaticNavigationProps } from 'routes/utils/prepareGetStaticNavigationProps'

export const Homepage = ({
  homepageData,
}: {
  homepageData?: ReactQuery.HomepageResponse
}) => {
  const { data: pageData, isLoading } = ReactQuery.useGetHomepage(
    {
      populate: {
        widgets: {
          on: {
            [STRAPI_WIDGETS.TRADE_ADVANTAGES_WIDGET]: {
              populate: '*',
            },
            [STRAPI_WIDGETS.HERO_WIDGET]: {
              populate: '*',
            },
            [STRAPI_WIDGETS.EXPERT_TESTIMONIALS_WIDGET]: {
              populate: {
                testimonials: {
                  populate: '*',
                },
              },
            },
            [STRAPI_WIDGETS.BRANDS_WIDGET]: {
              populate: {
                brands: {
                  populate: '*',
                },
              },
            },
            [STRAPI_WIDGETS.FEATURED_EXPERTS_WIDGET]: {
              populate: {
                experts: {
                  populate: '*',
                },
              },
            },
            [STRAPI_WIDGETS.TRENDING_PRODUCTS_WIDGET]: {
              populate: {
                products: {
                  populate: {
                    product: {
                      populate: {
                        images: true,
                        productVariants: {
                          populate: {
                            images: true,
                            leadProductImage: true,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
            [STRAPI_WIDGETS.FEATURED_CATEGORIES_WIDGET]: {
              populate: {
                categories: {
                  populate: '*',
                },
              },
            },
          },
        },
      } as never,
    },
    {
      query: {
        initialData: homepageData,
      },
    }
  )

  const widgets = pageData?.data?.widgets ?? []

  return (
    <Stack w="100%" gap={8}>
      {isLoading && <LoadingOverlay />}
      {widgets.map((widget) => {
        if (
          // eslint-disable-next-line no-underscore-dangle
          widget.__component === STRAPI_WIDGETS.HERO_WIDGET
        ) {
          return (
            <Box key={widget.id}>
              <StrapiHeroWidget {...widget} />
            </Box>
          )
        } // eslint-disable-next-line no-underscore-dangle
        if (widget.__component === STRAPI_WIDGETS.TRADE_ADVANTAGES_WIDGET) {
          return (
            <Box key={widget.id}>
              <StrapiTradeAdvantagesWidget {...widget} />
            </Box>
          )
        }
        // eslint-disable-next-line no-underscore-dangle
        if (widget.__component === STRAPI_WIDGETS.EXPERT_TESTIMONIALS_WIDGET) {
          return (
            <Box key={widget.id}>
              <StrapiExpertTestimonialsWidget {...widget} />
            </Box>
          )
        }

        // eslint-disable-next-line no-underscore-dangle
        if (widget.__component === STRAPI_WIDGETS.BRANDS_WIDGET) {
          return (
            <Box key={widget.id}>
              <StrapiBrandsWidget {...widget} />
            </Box>
          )
        }
        // eslint-disable-next-line no-underscore-dangle
        if (widget.__component === STRAPI_WIDGETS.FEATURED_EXPERTS_WIDGET) {
          return (
            <Box key={widget.id}>
              <StrapiFeaturedExpertsWidget {...widget} />
            </Box>
          )
        }

        // eslint-disable-next-line no-underscore-dangle
        if (widget.__component === STRAPI_WIDGETS.TRENDING_PRODUCTS_WIDGET) {
          return (
            <Box key={widget.id}>
              <StrapiTrendingProductsWidget {...widget} />
            </Box>
          )
        }

        // eslint-disable-next-line no-underscore-dangle
        if (widget.__component === STRAPI_WIDGETS.FEATURED_CATEGORIES_WIDGET) {
          return (
            <Box key={widget.id}>
              <StrapiFeaturedCategoriesWidget {...widget} />
            </Box>
          )
        }

        return null
      })}
    </Stack>
  )
}

export function getStaticProps() {
  return prepareGetStaticNavigationProps()
}

Homepage.seoProps = {
  description:
    "Book online consultations with the world's top interior designers and shop the best vintage and contemporary furniture, decor, lighting, rugs, and more handpicked by our Experts.",
  title:
    'The Expert: Online Interior Design Consultations & Furniture and Décor',
}

Homepage.getLayout = (page) =>
  getPublicLayout(page, {
    isFullWidth: true,
  })
